import * as React from "react";

import { Column } from "@tanstack/react-table";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import { CheckIcon } from "@radix-ui/react-icons";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";

import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils/ui";
import { ChevronDown, X } from "lucide-react";
import { Checkbox } from "../ui/checkbox";

export interface DataTableFilterOptions {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  color?: string | null;
}

interface DataTableSelectFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: DataTableFilterOptions[];
}

interface DataTableSingleSelectFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: DataTableFilterOptions[];
  includeAllOption?: boolean;
}

export function DataTableSelectFilter<TData, TValue>({
  column,
  title,
  options,
}: DataTableSelectFilterProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false);
  const selectedValues = new Set(column?.getFilterValue() as string[]);
  const updateSelectedValues = (nextValues: Set<string>) => {
    const filterValues = Array.from(nextValues);
    column?.setFilterValue(filterValues.length ? filterValues : undefined);
  };

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className="min-w-40 h-fit hover:bg-inherit hover:border-primary"
        >
          {selectedValues?.size === 0 && (
            <span className="opacity-50 w-full text-start">Select {title}</span>
          )}
          {selectedValues?.size > 0 && (
            <div className="flex w-full flex-wrap gap-1">
              {options
                .filter((option) => selectedValues.has(option.value))
                .map((option) => (
                  <Badge
                    variant="secondary"
                    key={option.value}
                    className="rounded-sm px-1 font-normal hover:cursor-default"
                  >
                    {option.color && (
                      <span
                        className="mr-1 h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    {option.label}
                    <span
                      className="hover:cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newSelectedValues = new Set(selectedValues);
                        newSelectedValues.delete(option.value);
                        updateSelectedValues(newSelectedValues);
                      }}
                    >
                      <X className="opacity-50 size-0.5 ml-1" />
                    </span>
                  </Badge>
                ))}
            </div>
          )}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-full" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newSelectedValues = new Set(selectedValues);
                      if (isSelected) {
                        newSelectedValues.delete(option.value);
                      } else {
                        newSelectedValues.add(option.value);
                      }
                      updateSelectedValues(newSelectedValues);
                      setOpen(false);
                    }}
                    className="gap-3"
                  >
                    <span
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {
                          const newSelectedValues = new Set(selectedValues);
                          if (isSelected) {
                            newSelectedValues.delete(option.value);
                          } else {
                            newSelectedValues.add(option.value);
                          }
                          updateSelectedValues(newSelectedValues);
                        }}
                      />
                    </span>
                    {option.color && (
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    {option.icon && (
                      <option.icon className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      column?.setFilterValue(undefined);
                      setOpen(false);
                    }}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function DataTableSingleSelectFilter<TData, TValue>({
  column,
  title,
  options,
  includeAllOption = true,
}: DataTableSingleSelectFilterProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false);
  const selectedValue = column?.getFilterValue() as string | undefined;
  const selectedOption = options.find((option) => option.value === selectedValue);
  const buttonLabel = selectedOption
    ? `${title}: ${selectedOption.label}`
    : `${title}: All`;

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className="min-w-40 h-fit hover:bg-inherit hover:border-primary justify-between"
        >
          <span className="flex items-center gap-2">
            {selectedOption?.icon && (
              <selectedOption.icon className="h-4 w-4 text-muted-foreground" />
            )}
            {buttonLabel}
          </span>
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-full" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {includeAllOption && (
                <CommandItem
                  value="all"
                  onSelect={() => {
                    column?.setFilterValue(undefined);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 h-4 w-4",
                      !selectedValue ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span>All</span>
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    column?.setFilterValue(option.value);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 h-4 w-4",
                      option.value === selectedValue
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {option.icon && (
                    <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  )}
                  <span>{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
